import { useGetTestsQuery } from 'app/services/tools';
import { Tool, ToolTest } from 'app/types/tool';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';

import { Column, ColumnTypes, DataTable } from 'components';
import saveAs from 'file-saver';
import { useTranslation } from 'react-i18next';
import { globalActions } from 'app/services/global/slice';
import { Drawers } from 'app/types/drawer';
import { useDispatch } from 'react-redux';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { User } from 'constants/index';
import DeleteTest from './DeleteButton';

type TestsTableProps = {
  tool?: Tool;
  // Pre-fetched test history (e.g. from the public tool overview endpoint) - when
  // provided, the authenticated per-tool query below is skipped entirely.
  data?: ToolTest[];
  // Hides the edit/delete actions - used by the public, unauthenticated
  // /tools/overview/:barcode page. Report download and row click stay available.
  readonly?: boolean;
};

const TestsTable = ({ tool, data: dataOverride, readonly }: TestsTableProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: fetchedData, isLoading, isFetching } = useGetTestsQuery(tool?.Id ?? 0, { skip: !tool || !!dataOverride });
  const data = dataOverride ?? fetchedData;
  const hasManageTestsPermission = useHasPermission(User.Permissions.MANAGE_TOOL_TEST);
  const canManageTests = !readonly && hasManageTestsPermission;

  const handleDownload = (test: ToolTest) => {
    if (!test.ReportUrl) return;
    const fileName = test.ReportFileName ? test.ReportFileName.replaceAll('"', '') : 'report';
    saveAs(test.ReportUrl, fileName);
  };

  const handleClick = (test: ToolTest) => {
    dispatch(globalActions.openDrawer(
      {
        key: Drawers.EDIT_TOOL_TEST,
        props: { toolTest: test }
      }));
  };

  const handleViewClick = (test: ToolTest) => {
    dispatch(globalActions.openDrawer(
      {
        key: Drawers.VIEW_TOOL_TEST,
        props: { test: test },
        readonly: true
      }));
  };

  const columns: Column[] = [
    {
      key: 'ExecutedAt',
      title: t('entities.tooltest.executedat'),
      type: ColumnTypes.DATE,
    },
    {
      key: 'IsSuccessful',
      title: t('entities.tooltest.issuccessful'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'IsSuccessful', value: true, color: 'success', message: t('entities.tooltest.result.success') },
        { key: 'IsSuccessful', value: false, color: 'error', message: t('entities.tooltest.result.failure') },
      ]
    },
    {
      key: 'Comment',
      title: t('entities.tooltest.comment'),
      type: ColumnTypes.TEXT,
      useEllipsis: true
    },
    {
      key: 'ExecutorName',
      title: t('entities.tooltest.executorname'),
      type: ColumnTypes.CUSTOM,
      renderAs: (test: ToolTest) => (
        <>
          <div>{test.ExecutorName}</div>
          {test.CompanyName && <div>{test.CompanyName}</div>}
        </>
      ),
    },
  ];

  const actions: any[] = [ {
    icon: <DownloadOutlined />,
    toolTip: t('entities.tooltest.reportfile'),
    onClick: handleDownload,
    hidden: (entity: ToolTest) => !entity.ReportUrl
  } ];

  if (canManageTests) {
    actions.unshift((test: ToolTest) => <DeleteTest test={test} />);

    actions.unshift({
      icon: <EditOutlined />,
      toolTip: t('common.editbytype', { type: t('entities.tooltest.title').toLowerCase() }),
      onClick: handleClick
    });
  }

  columns.push(
    {
      key: 'Action',
      title: t('common.action'),
      type: ColumnTypes.ACTIONS,
      actions: actions
    },
  );

  return <DataTable
    data={data || []}
    columns={columns}
    isLoading={isLoading || isFetching}
    pagination={true}
    tableKey="tooltests"
    handleRowClick={handleViewClick}
  />;
};

export default TestsTable;

